import { mergeScorecardHalves } from 'app/pages/tpoPortal/commandCenterHome/CommandCenterHome/fileQualityScorecard.utils';
import { aeBrokerQualityScorecardApi, aeBrokersApi } from 'app/services/api';
import { isNil } from 'lodash';

import { catchAsyncActionError } from 'utils/action';
import { createNamedActions } from 'utils/namedActionTypesProxy';

/**
 * Internal AE broker-quality coaching read (DEV-3781). The page is DARK until a
 * backend feature toggle is on: when off, every endpoint 404s. We first fetch the
 * AE's broker roster, then fan out per broker to the two scorecard gateways
 * (Underwriting + Loans) in parallel and merge each broker's two halves with the
 * same per-key logic the broker-facing zone uses (DEV-3777). ANY per-request
 * error (404 flag-off, 403 auth gap, network) is swallowed to null so the page
 * degrades to "not available" rather than surfacing an error toast or the global
 * forbidden page-reload loop. When the roster itself is empty/absent the page is
 * hidden.
 */
const SKIP_FORBIDDEN_CONFIG = { skipResponseInterceptor: { forbiddenError: true } };

export const AE_COMMAND_CENTER_QUALITY_ACTION_TYPE = createNamedActions('aeCommandCenterQuality', {
  SET_BROKERS: 'SET_BROKERS',
});

// Resolves a request to its response data, or null on any failure (flag-off 404,
// 403 auth gap, network) — the page must hide/degrade rather than error.
const fetchOrNull = async request => {
  try {
    const { data } = await request();

    return data ?? null;
  } catch {
    return null;
  }
};

// Fetches one broker's two scorecard halves in parallel and merges them. A half
// that 404s / errors contributes nothing; both absent → scorecard null.
const fetchBrokerScorecard = async subCompanyId => {
  const [underwriting, loans] = await Promise.all([
    fetchOrNull(() =>
      aeBrokerQualityScorecardApi.getUnderwritingScorecard({
        subCompanyId,
        config: SKIP_FORBIDDEN_CONFIG,
      }),
    ),
    fetchOrNull(() =>
      aeBrokerQualityScorecardApi.getLoansScorecard({
        subCompanyId,
        config: SKIP_FORBIDDEN_CONFIG,
      }),
    ),
  ]);

  return mergeScorecardHalves({ underwriting, loans });
};

export const getAeCommandCenterQualityAction = catchAsyncActionError(() => async dispatch => {
  const roster = await fetchOrNull(() =>
    aeBrokersApi.getMyBrokers({ config: SKIP_FORBIDDEN_CONFIG }),
  );

  // Roster 404 (flag off) / 403 (auth gap) → null payload → page hidden. A loaded
  // roster (even an empty broker list) → an array → the page renders its honest
  // empty state instead.
  const brokersWithScorecards = isNil(roster)
    ? null
    : await Promise.all(
        (roster.brokers ?? []).map(async broker => ({
          ...broker,
          scorecard: await fetchBrokerScorecard(broker.subCompanyId),
        })),
      );

  return dispatch({
    type: AE_COMMAND_CENTER_QUALITY_ACTION_TYPE.SET_BROKERS,
    payload: brokersWithScorecards,
  });
});
